import {
	children,
	createSignal,
	createUniqueId,
	For,
	type JSX,
	onCleanup,
	onMount,
	type ParentComponent,
} from "solid-js";
import "./Tabs.css";

export { TabLabel } from "./TabLabel";

export type TabDescriptor = {
	id: string;
	label: string | JSX.Element;
	disabled?: boolean;
	class?: string;
	content: JSX.Element;
};

export const Tab = (props: {
	id: string;
	label: string | JSX.Element;
	disabled?: boolean;
	class?: string;
	children: JSX.Element;
}): JSX.Element =>
	({
		id: props.id,
		label: props.label,
		disabled: props.disabled,
		class: props.class,
		content: props.children,
	}) as unknown as JSX.Element;

type TabsProps = {
	id?: string;
	class?: string;
	classList?: Record<string, boolean>;
	style?: JSX.CSSProperties;
	/**
	 * Visual style:
	 * - `card`: bordered strip (Boards and similar)
	 * - `default`: text tabs with underline
	 * - `default-inverted`: same chrome as `card`, but white inactive tabs and grey active tab
	 * - `borderless` / `separate`: new-pool import flows
	 */
	variant?: "card" | "default" | "default-inverted" | "borderless" | "separate";
	theme?: "standard" | "dark";
	/**
	 * How a `card` strip marks the active tab: `raised` lifts it as a tab shape
	 * out of the strip, `underline` keeps the tabs flat and underlines it. The
	 * dark theme is always underlined.
	 * @default "raised"
	 */
	tabIndicator?: "raised" | "underline";
	presentation?: "standard" | "popover";
	tabListStyle?: "flex-grow";
	/** Accessible name for the tab list when there is no visible group label. */
	"aria-label"?: string;
	/** Id of the element that labels this tab list (e.g. section heading). Overrides `aria-label` if both are set. */
	"aria-labelledby"?: string;
	selectedTab?: string;
	onTabSelected?: (tabId: string) => void;
	/** Key to use for the query string parameter. If undefined don't update the querystring. */
	queryStringKey?: string;
};

/**
 * Tab strip and panels. Implements the [APG tabs pattern](https://www.w3.org/WAI/ARIA/apg/patterns/tabs/)
 * (`tablist` / `tab` / `tabpanel`, roving tabindex, arrow keys skip disabled tabs).
 *
 * Uncontrolled (defaults to first enabled tab):
 * ```tsx
 * <Tabs aria-label="Settings">
 *   <Tab id="first" label="First">…</Tab>
 *   <Tab id="second" label="Second">…</Tab>
 * </Tabs>
 * ```
 *
 * Controlled:
 * ```tsx
 * const [tab, setTab] = createSignal("first");
 * <Tabs selectedTab={tab()} onTabSelected={setTab}>
 *   …
 * </Tabs>
 * ```
 */
export const Tabs: ParentComponent<TabsProps> = (props) => {
	const resolved = children(() => props.children);
	const tabsList = () =>
		(
			resolved.toArray() as unknown as (TabDescriptor | null | undefined)[]
		).filter(
			(tab): tab is TabDescriptor =>
				tab != null &&
				typeof tab === "object" &&
				typeof tab.id === "string" &&
				tab.id !== "" &&
				"label" in tab &&
				"content" in tab,
		);

	const instanceId = createUniqueId();
	const tabDomId = (tabId: string) => `${instanceId}-tab-${tabId}`;
	const panelDomId = (tabId: string) => `${instanceId}-panel-${tabId}`;

	const [internalTab, setInternalTab] = createSignal<string>("");

	const isControlled = () => props.selectedTab !== undefined;

	const tabIdFromQuery = () => {
		const key = props.queryStringKey;
		if (!key) {
			return undefined;
		}
		const value = new URLSearchParams(window.location.search).get(key);
		return value === null || value === "" ? undefined : value;
	};

	const enabledTabs = () => tabsList().filter((t) => !t.disabled);

	const effectiveVariant = () => props.variant ?? "card";

	const firstEnabledId = () => enabledTabs()[0]?.id ?? tabsList()[0]?.id ?? "";

	const currentTabId = () => {
		const list = tabsList();
		const pick = (id: string | undefined) => {
			if (id === undefined || id === "") return firstEnabledId();
			const row = list.find((t) => t.id === id);
			if (!row) return firstEnabledId();
			if (row.disabled) return firstEnabledId();
			return id;
		};

		if (isControlled()) {
			return pick(props.selectedTab);
		}
		const internal = internalTab();
		if (internal !== "") {
			return pick(internal);
		}
		const fromQuery = tabIdFromQuery();
		if (fromQuery !== undefined) {
			return pick(fromQuery);
		}
		return firstEnabledId();
	};

	const syncTabFromQuery = () => {
		if (!props.queryStringKey) {
			return;
		}
		const fromQuery = tabIdFromQuery();
		if (fromQuery === undefined) {
			if (!isControlled()) {
				setInternalTab("");
			}
			return;
		}
		if (!isControlled()) {
			setInternalTab(fromQuery);
		} else {
			props.onTabSelected?.(fromQuery);
		}
	};

	onMount(() => {
		if (!props.queryStringKey) {
			return;
		}
		window.addEventListener("popstate", syncTabFromQuery);
		onCleanup(() => window.removeEventListener("popstate", syncTabFromQuery));
	});

	const selectTab = (tab: TabDescriptor) => {
		const row = tabsList().find((t) => t.id === tab.id);
		if (row?.disabled) return;
		if (!isControlled()) setInternalTab(tab.id);
		if (props.queryStringKey) {
			const url = new URL(window.location.href);
			url.searchParams.set(props.queryStringKey, tab.id);
			window.history.pushState({}, "", url.toString());
		}
		props.onTabSelected?.(tab.id);
	};

	const focusTabButton = (tabId: string) => {
		queueMicrotask(() => document.getElementById(tabDomId(tabId))?.focus());
	};

	const moveSelection = (tabId: string, delta: number) => {
		const enabled = enabledTabs();
		const curIdx = enabled.findIndex((t) => t.id === tabId);
		if (curIdx === -1 || enabled.length === 0) return;
		const nextIdx = (curIdx + delta + enabled.length) % enabled.length;
		const next = enabled[nextIdx];
		if (next) {
			selectTab(next);
			focusTabButton(next.id);
		}
	};

	const handleTabKeyDown = (e: KeyboardEvent, tabId: string) => {
		const enabled = enabledTabs();
		if (enabled.length === 0) return;

		switch (e.key) {
			case "ArrowRight":
				e.preventDefault();
				moveSelection(tabId, 1);
				break;
			case "ArrowLeft":
				e.preventDefault();
				moveSelection(tabId, -1);
				break;
			case "Home":
				e.preventDefault();
				if (enabled[0]) {
					selectTab(enabled[0]);
					focusTabButton(enabled[0].id);
				}
				break;
			case "End": {
				e.preventDefault();
				const last = enabled[enabled.length - 1];
				if (last) {
					selectTab(last);
					focusTabButton(last.id);
				}
				break;
			}
			default:
				break;
		}
	};

	const activeIndex = () =>
		tabsList().findIndex((t) => t.id === currentTabId());

	return (
		<div
			id={props.id}
			class={`gvtui-card-tabs gvtui-card-tabs--${effectiveVariant()} ${props.class ?? ""}`}
			classList={{
				...props.classList,
				"gvtui-card-tabs--dark": props.theme === "dark",
				"gvtui-card-tabs--underlined":
					props.tabIndicator === "underline" || props.theme === "dark",
				"gvtui-card-tabs--popover": props.presentation === "popover",
				"gvtui-card-tabs--first-active": activeIndex() === 0,
				"gvtui-card-tabs--last-active":
					activeIndex() >= 0 && activeIndex() === tabsList().length - 1,
			}}
			style={props.style}
		>
			<div
				class="gvtui-card-tabs__tab-list"
				role="tablist"
				aria-label={props["aria-label"]}
				aria-labelledby={props["aria-labelledby"]}
			>
				<For each={tabsList()}>
					{(tab) => (
						<button
							type="button"
							id={tabDomId(tab.id)}
							role="tab"
							disabled={!!tab.disabled}
							tabIndex={currentTabId() === tab.id ? 0 : -1}
							aria-selected={currentTabId() === tab.id}
							aria-controls={panelDomId(tab.id)}
							class="gvtui-card-tabs__tab"
							classList={{
								"gvtui-card-tabs__tab--flex-grow":
									props.tabListStyle === "flex-grow",
								"gvtui-card-tabs__tab--active": currentTabId() === tab.id,
							}}
							onClick={() => selectTab(tab)}
							onKeyDown={(e) => handleTabKeyDown(e, tab.id)}
						>
							{tab.label}
						</button>
					)}
				</For>
			</div>
			<For each={tabsList()}>
				{(tab) => (
					<div
						id={panelDomId(tab.id)}
						role="tabpanel"
						aria-labelledby={tabDomId(tab.id)}
						hidden={currentTabId() !== tab.id}
						class={`gvtui-card-tabs__panel ${tab.class ?? ""}`}
					>
						{tab.content}
					</div>
				)}
			</For>
		</div>
	);
};
